import React from 'react';
import { motion } from 'framer-motion';
import { CodeBracketIcon, DevicePhoneMobileIcon, PaintBrushIcon, AcademicCapIcon, CalendarIcon, MapPinIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import TechStack from './TechStack';

const About = () => {
  const skills = [
    {
      icon: <CodeBracketIcon className="w-8 h-8" />,
      title: "Frontend Development",
      description: "React, JavaScript, HTML5, CSS3, Tailwind CSS",
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      icon: <DevicePhoneMobileIcon className="w-8 h-8" />,
      title: "Backend Development", 
      description: "Node.js, Express.js, Python, MySQL",
      gradient: "from-green-500 to-emerald-500"
    },
    {
      icon: <PaintBrushIcon className="w-8 h-8" />,
      title: "Problem Solving",
      description: "Data Structures, Algorithms, System Design",
      gradient: "from-purple-500 to-pink-500"
    }
  ];

  const education = [
    {
      degree: "BTech in Computer Science & Engineering",
      institution: "Aliah University",
      duration: "2022 - 2026 (Expected)",
      location: "Kolkata, West Bengal, India",
      grade: "CGPA - 7.83",
      icon: <AcademicCapIcon className="w-6 h-6" />
    },
    {
      degree: "ISC (Class XII)",
      institution: "Albany Hall Public School",
      duration: "2021-2022",
      location: "Kolkata, West Bengal, India",
      grade: "Percentage - 84.50%",
      icon: <AcademicCapIcon className="w-6 h-6" />
    },
    {
      degree: "ICSE (Class X)",
      institution: "Albany Hall Public School",
      duration: "2019-2020",
      location: "Kolkata, West Bengal, India",
      grade: "Percentage - 78%",
      icon: <AcademicCapIcon className="w-6 h-6" />
    }
  ];

  return (
    <section id="about" className="py-20 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-slate-900 dark:to-gray-800" />
      
      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,#fff,rgba(255,255,255,0.6))] dark:bg-grid-slate-700/25 dark:[mask-image:linear-gradient(0deg,rgba(255,255,255,0.1),rgba(255,255,255,0.5))]" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0.5 }}
            whileInView={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-medium mb-4"
          >
            <AcademicCapIcon className="w-4 h-4 mr-2" />
            Get to know me
          </motion.div>
          <h2 className="text-5xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent mb-6">
            About Me
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed mb-8">
            I'm a passionate Computer Science student and aspiring full-stack developer, currently pursuing my BTech at Aliah University. 
            I love creating digital solutions that combine beautiful design with powerful functionality.
          </p>
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              const link = document.createElement('a');
              link.href = '/KAIF_CV.pdf';
              link.download = 'Md_Kaif_Resume.pdf';
              link.click();
            }}
            className="inline-flex items-center gap-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <ArrowDownTrayIcon className="w-5 h-5" />
            <span>Download Resume</span>
          </motion.button>
        </motion.div>

        {/* Skills Section */}
        <div className="mb-20">
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl font-bold text-center mb-12 text-gray-900 dark:text-white"
          >
            What I Do
          </motion.h3>
          <div className="grid md:grid-cols-3 gap-8">
            {skills.map((skill, index) => (
              <motion.div
                key={skill.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                whileHover={{ y: -15, scale: 1.03, rotateY: 5 }}
                className="group bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl p-10 rounded-3xl shadow-xl hover:shadow-2xl hover:shadow-blue-500/20 transition-all duration-500 border border-white/30 dark:border-gray-700/50 relative overflow-hidden transform-gpu"
              >
                <div className={`absolute inset-0 bg-gradient-to-r ${skill.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
                <div className={`w-16 h-16 bg-gradient-to-r ${skill.gradient} rounded-xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  {skill.icon}
                </div>
                <h3 className="text-xl font-bold mb-4 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{skill.title}</h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{skill.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Education Section */}
        <div className="mb-20">
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl font-bold text-center mb-12 text-gray-900 dark:text-white"
          >
            Education
          </motion.h3>
          <div className="space-y-6">
            {education.map((edu, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="group bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-white/20 dark:border-gray-700/50 relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gray-50/50 dark:bg-gray-700/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  <div className="w-16 h-16 bg-gray-900 dark:bg-white rounded-xl flex items-center justify-center text-white dark:text-gray-900 flex-shrink-0 group-hover:scale-105 transition-transform duration-300">
                    {edu.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div>
                        <h4 className="text-xl font-bold mb-2 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors">{edu.degree}</h4>
                        <p className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-1">{edu.institution}</p>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                          <div className="flex items-center gap-1">
                            <CalendarIcon className="w-4 h-4" />
                            <span>{edu.duration}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <MapPinIcon className="w-4 h-4" />
                            <span>{edu.location}</span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-100 dark:bg-gray-800 px-4 py-2 rounded-full border border-gray-200 dark:border-gray-700">
                        <span className="text-gray-700 dark:text-gray-300 font-semibold text-sm">{edu.grade}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <TechStack />
      </div>
    </section>
  );
};

export default About;